import React from 'react';
import { Camera, Zap, Star } from 'lucide-react';

export default function FoodScanner({ lang }) {
  const t = {
    sectionTag: lang === 'bg' ? 'ТЕХНОЛОГИЯ' : 'TECHNOLOGY',
    headline: lang === 'bg' ? 'КИ СКЕНЕР ЗА СПОРТНО ХРАНЕНЕ' : 'AI SCANNER FOR SPORT NUTRITION',
    sub:
      lang === 'bg'
        ? 'Снимай храната си — КИ пресмята протеина, въглехидратите и горивото за следващата ти тренировка.'
        : 'Scan your food — AI calculates the protein, carbs and fuel for your next workout.',
    features: lang === 'bg'
      ? [
          { icon: Zap, text: 'Анализ на макроси оптимизиран за спортисти' },
          { icon: Star, text: 'Следи заредеността си преди мач или тренировка' },
          { icon: Camera, text: 'КИ препоръки за спортно хранене' },
        ]
      : [
          { icon: Zap, text: 'Macro analysis optimised for athletes' },
          { icon: Star, text: 'Track your fuel levels before a match or workout' },
          { icon: Camera, text: 'AI recommendations for sport nutrition' },
        ],
    cta: lang === 'bg' ? 'Опитай скенера' : 'Try scanner',
  };

  return (
    <section className="relative bg-[#0f172a] py-20 md:py-28 overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-orange-500/8 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-amber-400/8 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Content */}
          <div>
            <div className="inline-flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <Camera className="w-4 h-4 text-orange-400" />
              </div>
              <span className="text-orange-400 font-bold text-xs tracking-[0.3em] uppercase">
                {t.sectionTag}
              </span>
            </div>

            <h2 className="font-black uppercase text-3xl sm:text-4xl md:text-5xl leading-tight mb-6 text-white">
              {t.headline}
            </h2>

            <p className="text-slate-300 text-base md:text-xl leading-relaxed mb-10 font-medium">
              {t.sub}
            </p>

            <ul className="space-y-5 mb-10">
              {t.features.map((feat, i) => {
                const Icon = feat.icon;
                return (
                  <li key={i} className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-400/10 border border-orange-500/30 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-orange-400" />
                    </div>
                    <span className="text-slate-200 text-base md:text-lg font-medium">
                      {feat.text}
                    </span>
                  </li>
                );
              })}
            </ul>

            <a
              href="/s"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-black tracking-widest uppercase text-white transition-all duration-300 hover:scale-105 shadow-xl shadow-orange-500/30 border-2 border-orange-500 hover:bg-orange-500"
            >
              <Camera className="w-5 h-5" />
              {t.cta}
            </a>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-black/60 border border-slate-700/50">
              <img
                src="https://images.unsplash.com/photo-1505576399279-565b52d4ac71?w=800&q=80"
                alt="AI Food Scanner"
                className="w-full h-64 md:h-96 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/80 via-transparent to-transparent" />

              {/* Floating analysis card */}
              <div className="absolute bottom-4 left-4 right-4 bg-[#0f172a]/90 backdrop-blur-sm rounded-2xl p-4 border border-orange-500/30">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-orange-400 font-bold text-sm uppercase tracking-wider">
                    {lang === 'bg' ? 'КИ Анализ' : 'AI Analysis'}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: lang === 'bg' ? 'Калории' : 'Calories', val: '420 kcal' },
                    { label: lang === 'bg' ? 'Протеин' : 'Protein', val: '32g' },
                    { label: lang === 'bg' ? 'Въглехидрати' : 'Carbs', val: '45g' },
                  ].map((m, i) => (
                    <div key={i} className="text-center">
                      <div className="text-white font-black text-sm">{m.val}</div>
                      <div className="text-slate-500 text-xs">{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Decorative dots */}
            <div className="absolute -top-6 -right-6 grid grid-cols-4 gap-2 opacity-30">
              {Array.from({ length: 16 }).map((_, i) => (
                <div key={i} className="w-1.5 h-1.5 rounded-full bg-orange-400" />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
